/** Minimum overlay visibility to prevent flicker (ms) */
export const PANEL_LOADER_MIN_MS = 320;

const DEFAULT_LOADER_MESSAGE = "Yükleniyor";

const LOADER_HTML =
  '<div class="panel-spa-loader-card">' +
  '<div class="panel-spa-spinner" aria-hidden="true">' +
  '<div class="panel-spa-spinner-ring"></div>' +
  '<div class="panel-spa-spinner-ring panel-spa-spinner-ring--inner"></div>' +
  "</div>" +
  `<span class="panel-spa-loader-text">${DEFAULT_LOADER_MESSAGE}</span>` +
  '<div class="panel-spa-loader-dots" aria-hidden="true">' +
  "<span></span><span></span><span></span>" +
  "</div>" +
  "</div>";

let loaderEl = null;
let theme = "light";
let currentMessage = DEFAULT_LOADER_MESSAGE;

function getLoaderTextEl() {
  return loaderEl?.querySelector(".panel-spa-loader-text") ?? null;
}

function applyLoaderMessage(message = currentMessage) {
  currentMessage = message || DEFAULT_LOADER_MESSAGE;
  const textEl = getLoaderTextEl();
  if (textEl) textEl.textContent = currentMessage;
}

function ensureLoader() {
  if (loaderEl) return loaderEl;

  loaderEl = document.createElement("div");
  loaderEl.className = "panel-spa-loader";
  loaderEl.setAttribute("role", "status");
  loaderEl.setAttribute("aria-live", "polite");
  loaderEl.setAttribute("aria-busy", "false");
  loaderEl.innerHTML = LOADER_HTML;
  document.body.appendChild(loaderEl);

  applyThemeClass();
  return loaderEl;
}

function applyThemeClass() {
  if (!loaderEl) return;
  loaderEl.classList.toggle("panel-spa-loader--dark", theme === "dark");
  loaderEl.classList.toggle("panel-spa-loader--light", theme !== "dark");
}

function getMainEl() {
  return document.getElementById("panel-main");
}

/**
 * Imperative panel loader — React re-render tetiklemez.
 * Header yüksekliği CSS --panel-header-h (56px) ile sabit; ResizeObserver kullanılmaz.
 */
export function createPanelLoader(options = {}) {
  if (typeof options.isDark === "boolean") {
    theme = options.isDark ? "dark" : "light";
    applyThemeClass();
  }

  let shownAt = 0;
  let hideTimer = null;

  const show = (options = {}) => {
    ensureLoader();
    applyThemeClass();

    if (typeof options.message === "string") {
      applyLoaderMessage(options.message);
    } else {
      applyLoaderMessage(currentMessage);
    }

    loaderEl.classList.toggle("panel-spa-loader--fullscreen", options.fullscreen === true);

    const main = getMainEl();
    if (main) main.classList.add("is-spa-loading");

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    shownAt = Date.now();
    loaderEl.setAttribute("aria-busy", "true");
    loaderEl.classList.add("is-visible");
  };

  const hide = () => {
    const elapsed = Date.now() - shownAt;
    const delay = Math.max(0, PANEL_LOADER_MIN_MS - elapsed);

    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      const main = getMainEl();
      if (main) main.classList.remove("is-spa-loading");
      if (loaderEl) {
        loaderEl.setAttribute("aria-busy", "false");
        loaderEl.classList.remove("is-visible");
        loaderEl.classList.remove("panel-spa-loader--fullscreen");
        applyLoaderMessage(DEFAULT_LOADER_MESSAGE);
      }
      hideTimer = null;
    }, delay);
  };

  const setMessage = (message) => {
    applyLoaderMessage(message);
  };

  const setTheme = (isDark) => {
    theme = isDark ? "dark" : "light";
    applyThemeClass();
  };

  const dispose = () => {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = null;
    const main = getMainEl();
    if (main) main.classList.remove("is-spa-loading");
    if (loaderEl) {
      loaderEl.classList.remove("is-visible");
      loaderEl.remove();
      loaderEl = null;
    }
  };

  return { show, hide, setTheme, setMessage, dispose };
}
